import { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import WeaverService from 'Services/WeaverService'
import { loadGraphDataFromNodes } from 'Services/GraphUtils';
import {
  castValue,
  getMemberKey,
  getNamespaces,
  getNamespaceLabel,
  getNamespaceValue,
  getRelationkeyLabel,
  getAttributeKeys,
  getNodeLabel,
  getUid
} from 'Services/WeaverUtils';
import { generateId, showErrorMessage } from './helpers';
import { setCurrentItem, setCreatingNode, setNodeFormInitialValues, setEditingNode } from 'Actions/explore';
import { Row, Col, Form, Layout, Input, Select, Button, Checkbox, InputNumber } from 'antd';
import { setSelectedGraphItems, updateGraphNodes } from 'Actions/graph'
import Feather from '@sysunite/react-feather'
import { NodePicker, EditableAttributeInput } from 'Components';
import SelectOrCreateRelation from './SelectOrCreateRelation'
import { StyledSectionContainer } from './styled'
import { CalendarOutlined, CheckSquareOutlined, FieldTimeOutlined, RedoOutlined } from '@ant-design/icons'
import { ATTRIBUTES } from 'Types'
import moment from 'moment';

const CreateNodeView = ({
  hideRelationLabels,
  isCreatingNode,
  nodeFormInitialValues,
  isEditingNode,
  setEditingNode,
  setCreatingNode,
  updateGraphNodes,
  setCurrentItem,
  setSelectedGraphItems,
  nodes,
  edges
}) => {

  const [nodeAmount, setNodeAmount] = useState(1)

  const [saving, setSaving] = useState(false)
  const [keepCreating, setKeepCreating] = useState(false)
  const [convertNamespaces, setConvertNamespaces] = useState(true)

  const formRef = useRef()
  const fieldRef = useRef()
  const realKey = getAttributeKeys()[0] || 'name'

  let toDestroy = []

  const getRelationData = (n) => {
    const relationsIn = n._relationsIn
    const ins = _.map(relationsIn, (relation, relationKey) => {
      relation._records.map((formField) => {
        const { fromNode, toNode } = formField
        formField.key = getNamespaceLabel(relationKey)
        formField.source = fromNode.id()
        formField.target = toNode.id()
      })
      return relation._records
    })

    const relationsOut = n._relations
    const outs = _.map(relationsOut, (relation, relationKey) => {
      relation._records.map((formField) => {
        const { fromNode, toNode } = formField
        formField.key = getNamespaceLabel(relationKey)
        formField.source = fromNode.id()
        formField.target = toNode.id()
        formField.oldTarget = toNode
      })
      return relation._records
    })

    return { relationsIn: _.flatten(ins), relationsOut: _.flatten(outs) }
  }

  const getAttributeData = (n) => {
    const attributes = n._attributes
    return _.map(attributes, (attribute, attributeKey) => {
      return attribute._records.map((record) => {
        return {
          key: attributeKey,
          attNode: record.attNode,
          value: Weaver.Attribute.evokeValue(record.value, record.dataType),
          dataType: record.dataType
        }
      })
    })
  }

  let initialFormData = {}

  if (isEditingNode) {
    const { relationsIn, relationsOut } = getRelationData(isEditingNode)
    initialFormData = {
      id: isEditingNode.id(),
      graph: isEditingNode.graph,
      attributes: _.flatten(getAttributeData(isEditingNode)),
      relationsIn,
      relationsOut,
    }
  } else {
    initialFormData = {
      id: generateId(),
      ...nodeFormInitialValues,
      attributes: [{ key: realKey, dataType: 'string' }],
      relationsIn: [],
      relationsOut: []
    }
  }

  useEffect(() => {
    fieldRef.current?.focus()
    formRef.current?.setFieldsValue(initialFormData)
    return () => setNodeFormInitialValues(undefined)
  }, []);

  const shouldUpdateTypes = (rels) => {
    if (rels !== undefined && rels.length > 0) {
      return rels.map((rel) => rel.keys.find((key) => memberKeys.includes(key)))
    } else {
      return false
    }
  }

  const getStateUpdateFunction = (node, newEdges) => (newState) => {
    if (node instanceof Weaver.AttributeNode) {
      return
    }

    let graphItems = {};
    if (node instanceof Weaver.RelationNode) {
      graphItems = {
        nodes,
        edges: {
          ...edges,
          ...newEdges
        }
      }
    } else {
      const id = getUid(node)
      graphItems = {
        nodes: {
          ...nodes,
          [id]: { ...nodes[id], ...newState }
        },
        edges: {
          ...edges,
          ...newEdges
        }
      }
    }
    updateGraphNodes(graphItems)
  }

  const parseEdgesForVis = (relNodes) => {
    let newEdges = {}

    const parseEdge = (relNode) => {
      const id = getUid(relNode)
      const key = relNode.key
      const sourceId = getUid(relNode.fromNode)
      const targetId = getUid(relNode.toNode)

      const selfReferenceSize = (10 * (_.filter(edges, (edge) =>
        (edge.from === sourceId && edge.to === targetId) && targetId === sourceId
      ).length)) + 20

      return {
        id,
        _id: id,
        from: sourceId,
        _from: sourceId,
        to: targetId,
        selfReference: {
          size: edges[id]?.selfReference?.size || selfReferenceSize
        },
        _to: targetId,
        label: hideRelationLabels ? ' ' : getRelationkeyLabel(key, id),
        _label: getRelationkeyLabel(key, id),
        graph: relNode.graph,
        hasRelations: _.size(relNode.relations()) > 0,
        key,
        physics: sourceId !== targetId,
        scaling: {
          label: false,
          max: 6
        }
      }
    }

    relNodes.map((relNode) => {
      newEdges[getUid(relNode)] = parseEdge(relNode)
    })

    return newEdges
  }

  const updateGraph = (rNodes) => {
    if (isEditingNode instanceof Weaver.AttributeNode) {
      return
    }

    const newEdges = parseEdgesForVis(rNodes)
    const setGraphState = getStateUpdateFunction(isEditingNode, newEdges)
    setGraphState({
      id: getUid(isEditingNode),
      label: getNodeLabel(isEditingNode)
    })

    let node;
    if (isEditingNode instanceof Weaver.RelationNode) {
      node = edges[getUid(isEditingNode)]
      setSelectedGraphItems({ edges: [getUid(isEditingNode)] })
    } else {
      node = nodes[getUid(isEditingNode)]
      setSelectedGraphItems({ nodes: [getUid(isEditingNode)] })
    }

    setCurrentItem({
      ...node,
      type: ATTRIBUTES,
      memberKey: getMemberKey(),
      viewType: undefined
    })
  }

  const createOrUpdateAttributes = (values, node) => {
    const toCreate = []
    const toRemove = []

    for (const recordA of values) {
      if (recordA.attNode === undefined) {
        toCreate.push(recordA)
        continue
      }
      for (const recordB of initialFormData.attributes) {
        if (recordA.attNode.equals(recordB.attNode) && (recordA.key !== recordB.key || recordA.value !== recordB.value)) {
          toRemove.push(recordB)
          toCreate.push(recordA)
        }
        // TODO: Handle updating val instead of removing & creating
      }
    }

    for (const record of toRemove) {
      node.attribute(record.key).remove(record)
    }

    toCreate.map(({ key, value, dataType }) => {
      value = Weaver.Attribute.typesTime.includes(dataType) ? value.format("HH:mm:ss") : value
      value = dataType === 'json' ? escapeInsideString(value) : value
      if ([ 'fileUid', 'fileId', 'wvr:fileId' ].includes(dataType)) {
        if (_.isObject(value)) {
          value = value[0].wvrFileId
        }
      }
      node.attribute(key).add(value, { dataType })
    })
  }

  const createRelationsIn = (values, toNode) => {
    let rNodes = []
    const sources = values.map((field) => {
      const { key, source } = field
      const [id, graph] = source.split('@')
      let fromNode = Weaver.Node.getFromGraph(id, graph)
      rNodes.push(fromNode.relation(getNamespaceValue(key)).add(toNode))
      return fromNode
    })
    return [sources, rNodes]
  }

  const createRelationsOut = (values, fromNode) => {
    let rNodes = []
    values.map((field) => {
      const { key, target } = field
      const [id, graph] = target.split('@')
      let toNode = Weaver.Node.getFromGraph(id, graph)
      rNodes.push(fromNode.relation(getNamespaceValue(key)).add(toNode))
      return fromNode
    })
    return rNodes
  }

  const updateRelationsIn = (values) => {
    let rNodes = []
    const toCreate = values.filter((field) => !field.fromNode)
    const toUpdate = _.intersectionWith(values, initialFormData.relationsIn, function (val, initial) {
      return (val?.relNode?.id() === initial?.relNode?.id()) && (val?.source !== initial?.source)
    });

    const createdSources = toCreate.map(({ key, source }) => {
      const [id, graph] = source.split('@')
      let fromNode = Weaver.Node.getFromGraph(id, graph)
      let rNode = fromNode.relation(key).add(isEditingNode)
      rNodes.push(rNode)
      return fromNode
    })

    const updatedSources = toUpdate.map((field) => {
      const { source, key, toNode } = field
      toDestroy.push(field)
      const [id, graph] = source.split('@')
      let sourceNode = Weaver.Node.getFromGraph(id, graph)
      let rNode = sourceNode.relation(getNamespaceValue(key)).add(toNode)
      rNodes.push(rNode)
      return sourceNode
    })

    return { sources: [...createdSources, ...updatedSources], rNodes }
  }

  const updateRelationsOut = (values) => {
    const toCreate = values.filter((field) => !field.fromNode)
    const toUpdate = _.intersectionWith(values, initialFormData.relationsOut, function (val, initial) {
      return (val?.relNode?.id() === initial?.relNode?.id()) && (val?.target !== initial?.target)
    });

    let rNodes = []

    toCreate.map(({ key, target }) => {
      const [id, graph] = target.split('@')
      const toNode = Weaver.Node.getFromGraph(id, graph)
      rNodes.push(isEditingNode.relation(key).add(toNode))
    })

    toUpdate.map(({ key, oldTarget, target }) => {
      const [id, graph] = target.split('@')
      const toNode = Weaver.Node.getFromGraph(id, graph)
      rNodes.push(isEditingNode.relation(getNamespaceValue(key)).update(oldTarget, toNode))
    })

    return rNodes
  }

  const updateNode = (values) => {
    let sources = []
    let toUpdate = []
    let rNodes = []
    !_.isEmpty(values.attributes) ? createOrUpdateAttributes(values.attributes, isEditingNode) : null
    !_.isEmpty(values.relationsIn) ? { sources, rNodes } = updateRelationsIn(values.relationsIn) : null
    !_.isEmpty(values.relationsOut) ? rNodes.push(...updateRelationsOut(values.relationsOut)) : null
    toUpdate.push(isEditingNode, ...sources)
    return [toUpdate, rNodes]
  }

  const createNode = (values, toCreate) => {
    const id = Weaver.identifierPrefix === '' ? values.id : Weaver.identifierPrefix + values.id
    _.trim(values.graph) === "" ? values.graph = undefined : null
    const node = nodeAmount > 1 ? new Weaver.Node(undefined, values.graph) : new Weaver.Node(id, values.graph)

    let rNodes = []
    let sources = []
    !_.isEmpty(values.attributes) ? createOrUpdateAttributes(values.attributes, node) : null
    !_.isEmpty(values.relationsIn) ? [sources, rNodes] = createRelationsIn(values.relationsIn, node) : null
    !_.isEmpty(values.relationsOut) ? rNodes.push(...createRelationsOut(values.relationsOut, node)) : null
    toCreate.push(node, ...sources)

    return [toCreate, rNodes]
  }

  const createNodeBatch = async (values) => {
    setSaving(true)
    let nodes = []
    let rNodes = []
    try {
      for (let i = 0; i < values.nodeAmount; i++) {
        if (isCreatingNode) {
          [nodes, rNodes] = createNode(values, nodes)
        } else {
          [nodes, rNodes] = updateNode(values)
        }
      }

      if (toDestroy.length > 0) {
        const relevantNodes = toDestroy.map((rec) => rec.relNode || rec.attNode)
        await Weaver.Node.batchDestroy(relevantNodes, { commitMessage: `Remove ${ toDestroy.length } relations and attributes` })
      }

      await Weaver.Node.batchSave(nodes, { commitMessage: `Edit ${ values.nodeAmount } nodes from the edit node view.` })

      const ids = nodes.map(({ nodeId }) => nodeId)
      const createdNodes = await new Weaver.Query()
        .restrict(ids)
        .inGraph(nodes[0].graph)
        .selectOut('*')
        .find()

      setSaving(false)

      if (!keepCreating) {
        close()
      } else {
        formRef.current?.resetFields()
        formRef.current?.setFieldsValue({ ...initialFormData, id: generateId() })
        fieldRef.current?.focus()
      }

      shouldUpdateTypes(values.relations) === true ? await WeaverService.updateTypesMap() : null

      isEditingNode
      ?
      updateGraph(rNodes)
      :
      updateGraphNodes(await loadGraphDataFromNodes(createdNodes))
    } catch (e) {
      showErrorMessage(e.message)
      setSaving(false)
    }
  }

  const close = () => {
    isCreatingNode ? setCreatingNode(false) : null
    isEditingNode ? setEditingNode(false) : null
  }

  const getDataType = (fieldKey) => {
    const { attributes } = formRef.current.getFieldsValue()
    return attributes[fieldKey]?.dataType
  }

  const setDataType = (i, dataType) => {
    const { attributes } = formRef.current.getFieldsValue()
    const value = castValue(dataType, attributes[i].value)
    attributes[i] = { ...attributes[i], value, dataType }
    return formRef.current.setFieldsValue({ attributes: [...attributes] })
  }

  const onKeyDown = ({ keyCode }) => keyCode === 27 ? ShortcutManager.eventsMap['esc']?.fn() : null

  const IdField = ({ disabled }) => {
    const prefixSelector = (
      <Form.Item name="idPrefix" noStyle>
        <Select defaultValue={Weaver.identifierPrefix}>
          {_.map(getNamespaces(), (value, key) => <Select.Option key={key} value={key}>{ key }</Select.Option>)}
        </Select>
      </Form.Item>
    )

    return (
      <Row>
        <Col style={{ flex: 23 }}>
          <Form.Item name="id">
            <Input
              addonBefore={_.trim(Weaver.identifierPrefix) != '' ? prefixSelector : null}
              data-testid="id"
              placeholder="Identifier (optional)"
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col style={{ flex: 1, display: 'flex', flexDirection: 'row-reverse' }}>
          <Form.Item>
            <Button
              onClick={() => formRef?.current?.setFieldsValue({ id: generateId() })}
              icon={<RedoOutlined />}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>
    )
  }

  const GraphField = ({ disabled }) => (
    <Form.Item name="graph">
      <Input
        data-testid="graph"
        placeholder="Graph (optional)"
        disabled={disabled}
      />
    </Form.Item>
  )

  const getFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const evtMap = {
    'fileUid': getFile,
    'fileId': getFile,
    'wvr:fileId': getFile,
  }

  const renderAttributeFieldItems = (fields, remove) => {
    return fields.map((field, index) => {
      return (
        <Form.Item
          data-testid="attribute-form-item"
          key={field.key}
        >
          <Row gutter={8} type="flex">
            <Col style={{ flex: 3 }}>
              <Form.Item name={[field.name, 'key']}>
                <Input data-testid="attr-key"/>
              </Form.Item>
            </Col>
            <Col style={{ flex: 3 }}>
              <Form.Item
                name={[field.name, 'value']}
                valuePropName={getDataType(index) === 'boolean' ? 'checked' : 'value'}
                getValueFromEvent={evtMap[getDataType(index)]}
              >
                <EditableAttributeInput
                  data-testid="attr-value"
                  onKeyDown={onKeyDown}
                  type={getDataType(index)}
                  valueKey={field.value}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col style={{ flex: 1 }}>
              <Form.Item name={[field.name, 'dataType']}>
                <Select
                  data-testid="attribute-type"
                  onChange={(val) => setDataType(index, val)}
                >
                  <Select.Option value="json"><span data-testid="json">JSON</span></Select.Option>
                  <Select.Option value="string"><span data-testid="string">Text</span></Select.Option>
                  <Select.Option value="multiline"><span data-testid="multiline">Multiline</span></Select.Option>
                  <Select.Option value="wvr:fileId"><span data-testid="file">File</span></Select.Option>
                  <Select.Option value="double"><span data-testid="double">Number</span></Select.Option>
                  <Select.Option value="time"><span data-testid="time"><FieldTimeOutlined /></span></Select.Option>
                  <Select.Option value="date"><span data-testid="datetime"><CalendarOutlined /></span></Select.Option>
                  <Select.Option value="boolean"><span data-testid="boolean"><CheckSquareOutlined /></span></Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Button
                data-testid="remove-attribute"
                type="link"
                onClick={ () => {
                  const attToDestroy = formRef.current?.getFieldValue('attributes')[field.name]
                  attToDestroy ? toDestroy.push(attToDestroy) : null
                  remove(field.name)
                }}
              >
                <Feather type="x" />
              </Button>
            </Col>
          </Row>
        </Form.Item>
      )
    }
  )}

  const AttributeFields = () => (
    <StyledSectionContainer>
      <h1>Attributes</h1>
      <br/>
      <Form.List name="attributes">
        {(fields, { add, remove }) => {
          return (
            <div>
              { renderAttributeFieldItems(fields, remove) }
              <Form.Item>
                <Button
                  data-testid="add-attribute"
                  onClick={() => add()}
                  type="dashed"
                  block
                >
                  <Feather type="plus" /> Add attribute
                </Button>
              </Form.Item>
            </div>
          )}
        }
      </Form.List>
    </StyledSectionContainer>
  )

  const renderRelationInFieldItems = (fields, remove) => {
    return fields.map((field, index) => {
      const disabled = initialFormData?.relationsIn[index] !== undefined
      return (
        <Form.Item key={field.key}>
          <Row gutter={8} type="flex">
            <Col style={{ flex: 1 }}>
              <Form.Item name={[field.name, 'source']} rules={[{ required: true, message: 'A source is required.' }]}>
                <NodePicker
                  data-testid={`relation-in-${field.name}-source`}
                  placeholder='Source'
                  style={{ width: '100%' }}
                  query={ new Weaver.Query().selectIn(getMemberKey()) }
                />
              </Form.Item>
            </Col>
            <Col style={{ flex: 1 }}>
              <Form.Item name={[field.name, 'key']} rules={[{ required: true, message: 'A relation key is required.' }]}>
                <SelectOrCreateRelation
                  placeholder='Relation Key'
                  style={{ width: '100%' }}
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
            <Col style={{ flex: 1 }}>
              <Form.Item name={[field.name, 'target']} rules={[{ required: true, message: 'A target is required.' }]}>
                <NodePicker
                  placeholder='Target'
                  style={{ width: '100%' }}
                  query={ new Weaver.Query().selectIn(getMemberKey()) }
                  disabled={true}
                />
              </Form.Item>
            </Col>
            <Col>
              <Button
                data-testid={`reset-relation-in-${field.name}`}
                type="link"
                onClick={ () => {
                  const currData = formRef?.current?.getFieldsValue()
                  currData.relationsIn[field.name] = initialFormData.relationsIn[field.name]
                  formRef?.current?.setFieldsValue(currData)
                }}
              >
                <RedoOutlined />
              </Button>
              <Button
                data-testid={`remove-relation-in-${field.name}`}
                type="link"
                onClick={ () => {
                  const relToDestroy = formRef.current?.getFieldValue('relationsIn')[field.name]
                  relToDestroy ? toDestroy.push(relToDestroy) : null
                  remove(field.name)
                }}
              >
                <Feather type="x" />
              </Button>
          </Col>
          </Row>
        </Form.Item>
    )}
  )}

  const RelationInFields = () => (
    <StyledSectionContainer>
      <h1>Incoming Relations</h1>
      <br/>
      <Form.List name="relationsIn">
        {(fields, { add, remove }) => {
          return (
            <div>
              { renderRelationInFieldItems(fields, remove) }
              <Form.Item>
                <Button
                  data-testid="add-relation-in"
                  onClick={() => add({ target: formRef.current?.getFieldValue('id') })}
                  type="dashed"
                  block
                >
                  <Feather type="plus" /> Add Relation In
                </Button>
              </Form.Item>
            </div>
          )}
        }
      </Form.List>
    </StyledSectionContainer>
  )

  const renderRelationOutFieldItems = (fields, remove) => {
    return fields.map((field, index) => {
      const disabled = initialFormData?.relationsOut[index] !== undefined
      return (
        <Form.Item key={field.key}>
          <Row gutter={8} type="flex">
            <Col style={{ flex: 1 }}>
              <Form.Item name={[field.name, 'source']} rules={[{ required: true, message: 'A source is required.' }]}>
                <NodePicker
                  style={{ width: '100%' }}
                  query={ new Weaver.Query().selectIn(getMemberKey()) }
                  disabled={true}
                />
              </Form.Item>
            </Col>
            <Col style={{ flex: 1 }}>
              <Form.Item name={[field.name, 'key']} rules={[{ required: true, message: 'A relation key is required.' }]}>
                <SelectOrCreateRelation
                  placeholder='Relation Key'
                  style={{ width: '100%' }}
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
            <Col style={{ flex: 1 }}>
              <Form.Item name={[field.name, 'target']} rules={[{ required: true, message: 'A target is required.' }]}>
                <NodePicker
                  data-testid={`relation-out-${field.name}-target`}
                  style={{ width: '100%' }}
                  query={ new Weaver.Query().selectIn(getMemberKey()) }
                />
              </Form.Item>
            </Col>
            <Col>
              <Button
              data-testid={`reset-relation-out-${field.name}`}
              type="link"
              onClick={ () => {
                const currData = formRef?.current?.getFieldsValue()
                currData.relationsOut[field.name] = initialFormData.relationsOut[field.name]
                formRef?.current?.setFieldsValue(currData)
              }}
            >
              <RedoOutlined />
            </Button>
            <Button
                data-testid={`remove-relation-out-${field.name}`}
                type="link"
                onClick={ () => {
                  const relToDestroy = formRef.current?.getFieldValue('relationsOut')[field.name]
                  relToDestroy ? toDestroy.push(relToDestroy) : null
                  remove(field.name)
                }}
              >
                <Feather type="x" />
              </Button>
            </Col>
          </Row>
        </Form.Item>
    )}
  )}

  const RelationOutFields = () => (
    <StyledSectionContainer>
      <h1>Outgoing Relations</h1>
      <br/>
      <Form.List name="relationsOut">
        {(fields, { add, remove }) => {
          return (
            <div>
              { renderRelationOutFieldItems(fields, remove) }
              <Form.Item>
                <Button
                  data-testid="add-relation-out"
                  onClick={() => add({ source: formRef.current?.getFieldValue('id') })}
                  type="dashed"
                  block
                >
                  <Feather type="plus" /> Add Relation Out
                </Button>
              </Form.Item>
            </div>
          )}
        }
      </Form.List>
    </StyledSectionContainer>
  )

  return (
    <Layout style={{ overflow: 'auto', borderRadius: '5px', background: 'var(--background)', padding: '16px' }}>
      <Row align="middle">
        <Col style={{ flex: 1 }}>
          <h1>{ isCreatingNode ? 'Create node' : 'Edit node' }</h1>
        </Col>
        <Col>
          <Button data-testid="close" onClick={close} size="large" type="link"><Feather type="x"/></Button>
        </Col>
      </Row>
      <Form
        ref={formRef}
        onFinish={async (values) => await createNodeBatch(values)}
      >
        <IdField disabled={(nodeAmount > 1 || isEditingNode !== false)}/>
        <GraphField disabled={isEditingNode !== false}/>
        <AttributeFields />
        <RelationInFields/>
        <RelationOutFields/>
        <Form.Item
          name="nodeAmount"
          initialValue={1}
          label="Number of Nodes"
          labelAlign="left"
          style={{float: 'left'}}
        >
          <InputNumber
            min={1}
            onChange={setNodeAmount}
            disabled={isEditingNode !== false}
          />
        </Form.Item>
        <Form.Item style={{ float: 'right' }}>
          <Checkbox
            data-testid="keep-creating"
            checked={keepCreating}
            onChange={({ target: { checked }}) => setKeepCreating(checked)}
            disabled={isEditingNode !== false}
          >
            Keep creating nodes
          </Checkbox>
          <Checkbox
            data-testid="auto-convert-namespace"
            checked={convertNamespaces}
            onChange={({ target: { checked }}) => setConvertNamespaces(checked)}
          >
            Convert namespace to full URI
          </Checkbox>
          <Button
            data-testid="save"
            htmlType="submit"
            loading={saving}
            type="primary"
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </Layout>
  )
}

const mapStateToProps = ({ explore, graph: { graphData }, graphFilter }) => ({
  nodes: graphData.nodes,
  edges: graphData.edges,
  hideRelationLabels: graphFilter.hideRelationLabels,
  memberKeys: explore.memberKeys,
  nodeFormInitialValues: explore.nodeFormInitialValues,
  isCreatingNode: explore.isCreatingNode,
  isEditingNode: explore.isEditingNode
})

export default connect(
  mapStateToProps,
  {
    updateGraphNodes,
    setCreatingNode,
    setEditingNode,
    setNodeFormInitialValues,
    setSelectedGraphItems,
    setCurrentItem
  }
)(CreateNodeView)
